import { useQuery } from "@tanstack/react-query";
import { backendApi } from "../../../../shared/api/backendApi";
import { getHttpStatus } from "../../../../shared/api/apiClient";
import type { FeatureFlagResponse } from "../../../../shared/models/FeatureFlag";

/**
 * Laedt alle Feature-Flags des aktuell eingeloggten Tenants.
 * Die Daten werden 5 Minuten gecacht, da sich die Modul-Auswahl waehrend einer Session nicht aendert.
 */
export const useFeatureFlags = () => {
  return useQuery<FeatureFlagResponse[], Error>({
    queryKey: ["feature-flags"],
    queryFn: () => backendApi.getFeatureFlags(),
    staleTime: 5 * 60 * 1000, // 5 Minuten
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export interface ModuleEnabledState {
  //Modul ist freigeschaltet und darf gerendert werden.
  isEnabled: boolean;
  //Status noch unbekannt -> weder Modul noch Sperr-Layout rendern.
  isLoading: boolean;
  //Status konnte wegen Server-/Netzwerkfehler nicht ermittelt werden.
  isError: boolean;
  //Erneuter Ladeversuch laeuft.
  isRetrying: boolean;
  //Feature-Flags erneut laden.
  retry: () => void;
}

//Prueft ob ein bestimmtes Modul fuer den aktuellen Tenant freigeschaltet ist.
 
export const useIsModuleEnabled = (moduleCode: string): ModuleEnabledState => {
  const { data: flags, isPending, error, refetch, isFetching } = useFeatureFlags();

  const base = {
    isRetrying: isFetching,
    retry: () => {
      void refetch();
    },
  };

  if (isPending) {
    return { ...base, isEnabled: false, isLoading: true, isError: false };
  }

  if (error) {
    const status = getHttpStatus(error);

    if (status === 401) {
      // Session ungueltig – Weiterleitung laeuft, bis dahin nichts rendern.
      return { ...base, isEnabled: false, isLoading: true, isError: false };
    }

    if (status === 403) {
      return { ...base, isEnabled: false, isLoading: false, isError: false };
    }

    return { ...base, isEnabled: false, isLoading: false, isError: true };
  }

  const flag = flags?.find((f) => f.moduleCode === moduleCode);
  return {
    ...base,
    isEnabled: flag?.enabled === true,
    isLoading: false,
    isError: false,
  };
};
